// Tool argument interfaces for ticket analysis
export interface ListDirToolArguments {
  directoryPath: string;
}

export interface ReadFileToolArguments {
  targetFile: string;
  shouldReadEntireFile?: boolean;
  startLine?: number;
  endLine?: number;
}

export interface RequestRecommendationToolArguments {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: Record<string, unknown>;
  reason: string;
}

export interface EditFileToolArguments {
  targetFile: string;
  codeEdit?: string;
}

export interface EditBusinessUnitPromptToolArguments {
  businessUnitId: number;
  promptEdits: string;
}

export interface GrepSearchToolArguments {
  query: string;
  caseSensitive?: boolean;
  includePattern?: string;
}

export interface FileSearchToolArguments {
  query: string;
  maxResults?: number;
}

export interface ExecuteQueryToolArguments {
  sqlQuery: string;
}

export interface GetClientInfoToolArguments {
  clientId: number;
  includeMessages?: boolean;
}

export interface MarkAnalysisCompleteToolArguments {
  summary: string;
  status: 'RESOLVED' | 'PARTIALLY_RESOLVED' | 'NEEDS_MANUAL_INTERVENTION' | 'INSUFFICIENT_INFORMATION';
  nextSteps?: string;
}

export interface TicketToolCall {
  id: string;
  type: 'function';
  function: {
    name:
      | 'listDir'
      | 'readFile'
      | 'editFile'
      | 'grepSearch'
      | 'fileSearch'
      | 'executeQuery'
      | 'getDatabaseSchema'
      | 'getClientInfo'
      | 'getBusinessUnitDetails'
      | 'requestRecommendation'
      | 'readLogs'
      | 'editBusinessUnitPrompt'
      | 'markAnalysisComplete';
    arguments: string;
  };
}

export type TicketEventType = 'ticket_edited' | 'ticket_analyzed_richard' | 'admin_assigned' | 'ticket_reopened';

export const TICKET_EVENT_NAME_PREFIX = 'event:';

export interface TicketEventMeta {
  type: TicketEventType;
  adminOnly: boolean;
  url?: string;
}

/** Decodes an event sentinel stored in `message.name`. Returns null when the message is not an event. */
export const parseTicketEventName = (name: string | undefined | null): TicketEventMeta | null => {
  if (!name?.startsWith(TICKET_EVENT_NAME_PREFIX)) return null;
  const rest = name.slice(TICKET_EVENT_NAME_PREFIX.length);
  const firstColon = rest.indexOf(':');
  const type = (firstColon >= 0 ? rest.slice(0, firstColon) : rest) as TicketEventType;
  const url = firstColon >= 0 ? rest.slice(firstColon + 1) : undefined;
  const adminOnly = type === 'ticket_analyzed_richard' || type === 'admin_assigned';
  return { type, adminOnly, url };
};

export interface TicketHistoryMessage {
  role: 'user' | 'assistant' | 'tool' | 'system';
  content: string | TicketMessageContent[];
  toolCalls?: TicketToolCall[];
  toolCallId?: string;
  name?: string;
  createdAt?: string;
}

export interface TicketMessageContent {
  type: 'text' | 'image_url' | 'document' | 'video';
  text?: string;
  image_url?: {
    url: string;
  };
  document?: {
    format: string;
    url: string;
    urlDirect?: string;
  };
  video?: {
    format: string;
    url: string;
    name?: string;
  };
}

export interface TicketUsage {
  model: string;
  inputTokens: number;
  outputTokens: number;
  cost: number;
}

export type TicketStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'CLOSED'
  | 'FAILED'
  | 'ANALYSIS_WITH_AI'
  | 'MANUAL_ANALYSIS_PENDING'
  | 'BLOCKED'
  | 'DISMISSED';

/**
 * Status values usable in the list filter. The two PENDING_* are virtual: they map to the real
 * PENDING status plus a condition on lastMessageWasSentByAdminUser, resolved server-side.
 */
export type TicketStatusFilter = TicketStatus | 'PENDING_NEW_MESSAGE' | 'PENDING_AWAITING_RESPONSE';

export const TICKET_MOTIVES = [
  'AI_CONTENT',
  'PRODUCT_MISSMATCH',
  'CRM_UPLOAD',
  'DELETE_TEMPLATE',
  'CAMPAIGN',
  'OTHER',
  'ROLLBAR',
] as const;

export type TicketMotive = (typeof TICKET_MOTIVES)[number];

/** Motivos seleccionables por el usuario. ROLLBAR es automático (lo asigna el webhook). */
export const SELECTABLE_TICKET_MOTIVES = TICKET_MOTIVES.filter((m) => m !== 'ROLLBAR');

export const TICKET_MOTIVE_LABELS: Record<TicketMotive, string> = {
  AI_CONTENT: 'Contenido o flujo de IA',
  PRODUCT_MISSMATCH: 'Producto/precio no calza',
  CRM_UPLOAD: 'Subida CRM',
  DELETE_TEMPLATE: 'Eliminar plantilla',
  CAMPAIGN: 'Campaña',
  OTHER: 'Otro',
  ROLLBAR: 'Rollbar (automático)',
};

export type TicketUrgency = 'LOW' | 'MEDIUM' | 'HIGH';

export const TICKET_URGENCIES: TicketUrgency[] = ['LOW', 'MEDIUM', 'HIGH'];

export const TICKET_URGENCY_LABELS: Record<TicketUrgency, string> = {
  LOW: 'Baja',
  MEDIUM: 'Media',
  HIGH: 'Alta',
};

export interface Ticket {
  id: number;
  title: string;
  description: string;
  lastMessage?: string;
  lastMessageWasSentByAdminUser?: boolean | null;
  status: TicketStatus;
  AIHistory?: TicketHistoryMessage[];
  AIUsages: TicketUsage[];
  userId: number;
  clientId?: number;
  businessUnitId?: number;
  rollbarItemId?: string | null;
  interestedUserIds?: number[];
  motive?: TicketMotive | null;
  urgency?: TicketUrgency | null;
  deadlineDate?: string | null;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: number;
    name: string;
  };
  businessUnit?: {
    id: number;
    name: string;
  };
}

export interface CreateTicketRequest {
  title: string;
  description: string;
  clientId?: number;
  businessUnitId?: number;
  motive: TicketMotive;
  urgency: TicketUrgency;
  deadlineDate?: string | null;
}

export interface AddMessageToTicketRequest {
  content: string | TicketMessageContent[];
  showToUser?: boolean;
}

export interface UpdateTicketRequest {
  title?: string;
  description?: string;
  motive?: TicketMotive | null;
  urgency?: TicketUrgency | null;
  deadlineDate?: string | null;
}

export interface UpdateTicketMessageRequest {
  content: string | TicketMessageContent[];
}

export interface SendTicketToRichardResponse {
  sessionId: string;
  url: string;
}
